import { useRef, useState } from "react";
import {
  ASK_FLASHDICE_API_ENDPOINT,
  ASK_FLASHDICE_MESSAGE_STAGGER_DELAY,
  ASK_FLASHDICE_MAX_NODES,
} from "../utils/askFlashDiceConstants";
import { StorageHelper } from "../utils/storageHelper";
import { authStreamFetch } from "../utils/authStreamFetch";
import { parseSqlExecutionFromMarkdown } from "../utils/markdownParser";

/**
 * @param {Function} dispatchMessages
 * @param {Function} setCurrentStreamingMessageId
 * @param {Function} [setSqlResults] - Optional. Called with SQL execution data parsed from markdown.
 * @param {((assistantMessageId: string, content: string) => void)|null} [onStreamComplete] - Optional. Called when streaming finishes with final message id and content.
 * Streaming hook: pass stream=false as the 6th argument to receive JSON { generated_sql, error } and show SQL only (no SSE).
 */
export const useAskFlashDiceStreaming = (
  dispatchMessages,
  setCurrentStreamingMessageId,
  setSqlResults,
  onStreamComplete
) => {

  const handleStreaming = async (userInput, apiMessages, sessionId, model, isRetry = false, stream = true) => {
    // Get mandatory fields from StorageHelper
    const user_db_id = StorageHelper.get("selected_user_db_id");
    const user_id = StorageHelper.get("userDetails")?.user_id;
    const db_type = "snowflake";
    const schema_name = StorageHelper.get("schemaInfo")?.schema_name;

    // Validate mandatory fields
    if (!user_db_id) {
      throw new Error("user_db_id is required. Please select a database.");
    }
    if (!user_id) {
      throw new Error("user_id is required. Please ensure you are logged in.");
    }
    if (!schema_name) {
      throw new Error("schema_name is required. Please ensure schema information is available.");
    }

    const requestPayload = {
      messages: apiMessages,
      max_nodes: ASK_FLASHDICE_MAX_NODES,
      stream: !!stream,
      session_id: sessionId != null && sessionId !== "" ? String(sessionId) : "default",
      user_db_id: user_db_id,
      user_id: user_id,
      db_type: db_type,
      schema_name: schema_name,
      model: model || null,
      is_retry: !!isRetry,
    };

    const assistantMessageId = (Date.now() + 1).toString();

    if (!stream) {
      dispatchMessages({
        type: "ADD_MESSAGE",
        message: { id: assistantMessageId, role: "assistant", content: "" },
      });
      setCurrentStreamingMessageId(assistantMessageId);

      let content = "";
      try {
        const response = await authStreamFetch(ASK_FLASHDICE_API_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...requestPayload, stream: false }),
        });
        if (!response.ok) {
          content = `Error: HTTP error! status: ${response.status}`;
          dispatchMessages({ type: "UPDATE_MESSAGE", id: assistantMessageId, content });
          if (typeof onStreamComplete === "function") {
            try {
              onStreamComplete(assistantMessageId, content);
            } catch (e) {
              console.warn("AskFlashDice onStreamComplete error:", e);
            }
          }
          return;
        }
        const data = await response.json();
        const sql = typeof data.generated_sql === "string" ? data.generated_sql : "";
        content = sql
          ? "```sql\n" + sql + "\n```"
          : data.error
            ? `❌ **Error:** ${data.error}`
            : "";
        dispatchMessages({ type: "UPDATE_MESSAGE", id: assistantMessageId, content });
        if (setSqlResults && sql) {
          setSqlResults(
            {
              success: false,
              data: [],
              columns: [],
              row_count: 0,
              execution_time: 0,
              error: "",
              sql,
              originalQuery: sql,
              source: "sql_only",
            },
            false
          );
        }
        if (typeof onStreamComplete === "function") {
          try {
            onStreamComplete(assistantMessageId, content ?? "");
          } catch (e) {
            console.warn("AskFlashDice onStreamComplete error:", e);
          }
        }
      } catch (err) {
        console.error("Non-stream chat API error:", err);
        content = `Error: ${err?.message || "Failed to get response from AI assistant"}`;
        dispatchMessages({ type: "UPDATE_MESSAGE", id: assistantMessageId, content });
        if (typeof onStreamComplete === "function") {
          try {
            onStreamComplete(assistantMessageId, content);
          } catch (e) {
            console.warn("AskFlashDice onStreamComplete error:", e);
          }
        }
      } finally {
        setCurrentStreamingMessageId(null);
      }
      return;
    }

    const response = await authStreamFetch(ASK_FLASHDICE_API_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestPayload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const assistantMessage = {
      id: assistantMessageId,
      role: "assistant",
      content: "",
    };

    dispatchMessages({ type: "ADD_MESSAGE", message: assistantMessage });
    setCurrentStreamingMessageId(assistantMessageId);

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let assistantContent = "";

    if (reader) {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6).trim();
            if (data === "[DONE]") {
              setCurrentStreamingMessageId(null);
              continue;
            }

            if (data.trim() && data.includes("{") && data.includes("}")) {
              try {
                const parsed = JSON.parse(data);

                if (parsed.choices?.[0]?.finish_reason === "stop") {
                  setCurrentStreamingMessageId(null);
                }
                if (parsed.choices?.[0]?.delta?.content) {
                  const content = parsed.choices[0].delta.content;
                  if (content) {
                    // OpenAI pattern: accumulate all content, including from "final" chunk
                    // The "final" chunk with finish_reason still contains delta.content
                    assistantContent += content;
                    
                    // Try to parse SQL execution data from accumulated markdown
                    // Store results but don't auto-open panel (only open when user runs from SQL editor)
                    if (setSqlResults) {
                      const sqlData = parseSqlExecutionFromMarkdown(assistantContent);
                      if (sqlData) {
                        setSqlResults({
                          success: sqlData.success,
                          data: sqlData.data || [],
                          columns: sqlData.columns || [],
                          row_count: sqlData.row_count || 0,
                          execution_time: sqlData.execution_time || 0,
                          error: sqlData.error || "",
                          sql: sqlData.sql || "",
                          source: "execute",
                        }, false); // Don't auto-open during streaming
                      }
                    }
                    
                    dispatchMessages({
                      type: "UPDATE_MESSAGE",
                      id: assistantMessageId,
                      content: assistantContent,
                    });
                  } else if (parsed.content) {
                    assistantContent += parsed.content;
                    
                    // Try to parse SQL execution data from accumulated markdown
                    // Store results but don't auto-open panel (only open when user runs from SQL editor)
                    if (setSqlResults) {
                      const sqlData = parseSqlExecutionFromMarkdown(assistantContent);
                      if (sqlData) {
                        setSqlResults({
                          success: sqlData.success,
                          data: sqlData.data || [],
                          columns: sqlData.columns || [],
                          row_count: sqlData.row_count || 0,
                          execution_time: sqlData.execution_time || 0,
                          error: sqlData.error || "",
                          sql: sqlData.sql || "",
                          source: "execute",
                        }, false); // Don't auto-open during streaming
                      }
                    }
                    
                    dispatchMessages({
                      type: "UPDATE_MESSAGE",
                      id: assistantMessageId,
                      content: assistantContent,
                    });
                  } else if (parsed.choices?.[0]?.message?.content) {
                    assistantContent = parsed.choices[0].message.content;
                    
                    // Try to parse SQL execution data from accumulated markdown
                    // Store results but don't auto-open panel (only open when user runs from SQL editor)
                    if (setSqlResults) {
                      const sqlData = parseSqlExecutionFromMarkdown(assistantContent);
                      if (sqlData) {
                        setSqlResults({
                          success: sqlData.success,
                          data: sqlData.data || [],
                          columns: sqlData.columns || [],
                          row_count: sqlData.row_count || 0,
                          execution_time: sqlData.execution_time || 0,
                          error: sqlData.error || "",
                          sql: sqlData.sql || "",
                          source: "execute",
                        }, false); // Don't auto-open during streaming
                      }
                    }
                    
                    dispatchMessages({
                      type: "UPDATE_MESSAGE",
                      id: assistantMessageId,
                      content: assistantContent,
                    });
                  }
                }
              } catch (e) {
                console.warn("Failed to parse SSE data:", e, line);
              }
            }
          }
        }
      }
    }

    // Parse SQL execution data from final accumulated content
    if (setSqlResults) {
      const sqlData = parseSqlExecutionFromMarkdown(assistantContent);
      if (sqlData) {
        setSqlResults({
          success: sqlData.success,
          data: sqlData.data || [],
          columns: sqlData.columns || [],
          row_count: sqlData.row_count || 0,
          execution_time: sqlData.execution_time || 0,
          error: sqlData.error || "",
          sql: sqlData.sql || "",
          // Streaming path currently only knows the displayed SQL; treat it as originalQuery as well.
          originalQuery: sqlData.originalQuery || sqlData.sql || "",
          source: "execute",
        }, false);
      }
    }

    if (typeof onStreamComplete === "function") {
      try {
        onStreamComplete(assistantMessageId, assistantContent ?? "");
      } catch (e) {
        console.warn("AskFlashDice onStreamComplete error:", e);
      }
    }

    setCurrentStreamingMessageId(null);
  };

  return { handleStreaming };
};

